/**
 * AssetManager - tracks which of the declared sprites loaded successfully.
 *
 * Why we need this: Phaser's loader will fire a 'loaderror' event if a file is
 * missing, but trying to use a missing texture later just renders an ugly green
 * "missing texture" box. We want to silently fall back to placeholders instead,
 * so the game stays playable when assets are partially missing or not yet
 * dropped in.
 *
 * For animated slots (chimp, spider), we load as a spritesheet using TILE_SIZE
 * frames. After load we read the actual frame count from the texture:
 *   - 1 frame → static image, no animation
 *   - 2 frames → 2-frame walk animation registered under `${key}-walk`
 * Consumers call `frameCount(key)` to decide which path to take.
 *
 * Singleton because asset availability is process-wide once loaded.
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '../config';
import { SPRITE_ASSETS } from './registry';

/** ms per frame for 2-frame walk animations. Tuned to feel like a step cadence. */
const WALK_FRAME_DURATION_MS = 240;

class _AssetManager {
  private available = new Set<string>();
  private failed = new Set<string>();
  private frames = new Map<string, number>();
  private warned = false;

  /** Queue all declared assets and wire up success/failure tracking. */
  preloadInto(scene: Phaser.Scene): void {
    for (const asset of SPRITE_ASSETS) {
      if (asset.animated) {
        // Spritesheet loader works for 1-frame PNGs too; frame count is read
        // from the texture post-load to decide whether to play an animation.
        scene.load.spritesheet(asset.key, asset.url, {
          frameWidth: TILE_SIZE,
          frameHeight: TILE_SIZE,
        });
      } else {
        scene.load.image(asset.key, asset.url);
      }
    }
    scene.load.on(Phaser.Loader.Events.FILE_COMPLETE, (key: string) => {
      this.available.add(key);
    });
    scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
      this.failed.add(file.key);
    });
  }

  /**
   * Run after preload: inspect each loaded animated asset, record its frame
   * count, and register a walk animation if it has 2+ frames.
   *
   * Must be called from a scene's create() (needs anims system available).
   */
  finalize(scene: Phaser.Scene): void {
    for (const asset of SPRITE_ASSETS) {
      if (!asset.animated || !this.available.has(asset.key)) continue;
      const tex = scene.textures.get(asset.key);
      // Phaser exposes a __BASE frame on every texture plus the real frames,
      // so an N-frame spritesheet has frameTotal === N + 1. Use getFrameNames
      // which returns only the real frames.
      const frameNames = tex.getFrameNames();
      const count = frameNames.length || 1;
      this.frames.set(asset.key, count);

      if (count >= 2) {
        const animKey = `${asset.key}-walk`;
        if (!scene.anims.exists(animKey)) {
          const frameMs = asset.animFrameMs ?? WALK_FRAME_DURATION_MS;
          scene.anims.create({
            key: animKey,
            frames: scene.anims.generateFrameNumbers(asset.key, { start: 0, end: count - 1 }),
            frameRate: 1000 / frameMs,
            repeat: -1,
          });
        }
      }
    }
  }

  /**
   * After preload, log a one-time summary of which assets fell back. Useful
   * during the art integration so you can spot a misnamed file quickly.
   */
  reportOnce(): void {
    if (this.warned) return;
    this.warned = true;
    if (this.failed.size > 0) {
      console.info(
        `splat-chimp: using placeholders for missing assets: ${Array.from(this.failed).join(', ')}`
      );
    }
  }

  has(key: string): boolean {
    return this.available.has(key);
  }

  /** Frame count for an animated key (1 = static, 2+ = animatable). 0 if not loaded. */
  frameCount(key: string): number {
    return this.frames.get(key) ?? 0;
  }

  /** Animation key registered for a multi-frame asset, or null. */
  walkAnimKey(key: string): string | null {
    return this.frameCount(key) >= 2 ? `${key}-walk` : null;
  }
}

export const AssetManager = new _AssetManager();
